import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowRight, Database, FileText, Layers } from 'lucide-react';
import { api } from '@/lib/api';

// Audit panel: what was imported, from which source model, bound to which model.
export function ImportHistoryDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.reportImportHistory(100).then(setRows).catch(() => setRows([]));
  }, [open]);

  const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || ''; } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[80vh] overflow-auto" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Import history</DialogTitle>
          <DialogDescription>Every report imported into this workspace — its source model and the model it was bound to.</DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          {rows === null ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="import-history-empty">No imports yet. Imported reports will be listed here.</p>
          ) : (
            <div className="space-y-2" data-testid="import-history-list">
              {rows.map((r) => {
                const src = r.source_model_ref || {};
                const srcName = src.model_name || src.model_id || 'unknown model';
                return (
                  <div key={r.import_id} className="rounded-xl border border-border/40 bg-muted/20 p-3" data-testid={`import-history-row-${r.import_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.report_name || 'Untitled report'}</p>
                        <div className="flex items-center flex-wrap gap-1.5 mt-1 text-[11px] text-muted-foreground">
                          <Database className="w-3 h-3" />
                          <span className="truncate max-w-[180px]" title={srcName}>{srcName}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span className="font-medium text-foreground truncate max-w-[180px]" title={r.target_model_name || r.target_model_id}>{r.target_model_name || r.target_model_id}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant="outline" className="text-[9px]">{r.import_kind === 'pack' ? <><Layers className="w-2.5 h-2.5 mr-1" /> pack</> : 'single'}</Badge>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{when(r.imported_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {r.created_model?.model_id && <Badge className="text-[9px] bg-primary/15 text-primary border-primary/30">created model</Badge>}
                      {r.fields_remapped > 0 && <Badge variant="outline" className="text-[9px]">{r.fields_remapped} field{r.fields_remapped > 1 ? 's' : ''} remapped</Badge>}
                      {r.fields_unresolved > 0 && <Badge className="text-[9px] bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30">{r.fields_unresolved} unresolved</Badge>}
                      {r.imported_by_email && <span className="text-[10px] text-muted-foreground truncate">by {r.imported_by_email}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
